import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  User,
  Lock,
  Bell,
  Globe,
  CreditCard,
  Shield,
  HelpCircle,
  LogOut,
  Clock,
  Building,
  Briefcase,
  Mail,
  Phone,
  CheckCircle2,
  Users,
  UserPlus,
  Trash2,
  FileText,
  Plus,
  FileCheck,
  AlertTriangle,
  ShieldCheck,
  QrCode,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/lib/auth-utils";
import { cn } from "@/lib/utils";

interface ProfileData {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  language: string;
  timezone: string;
  tenant_id?: string | null;
}

interface NotificationPrefs {
  email_notifications: boolean;
  push_notifications: boolean;
  case_updates: boolean;
  system_updates: boolean;
}

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = "Navegador Desconocido";
  let os = "OS Desconocido";

  // Browser detection
  if (ua.indexOf("Edg") > -1) browser = "Microsoft Edge";
  else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
  else if (ua.indexOf("Safari") > -1) browser = "Safari";
  else if (ua.indexOf("Firefox") > -1) browser = "Firefox";
  else if (ua.indexOf("MSIE") > -1 || !!(document as any).documentMode) browser = "IE";

  // OS detection
  if (ua.indexOf("Win") > -1) os = "Windows";
  else if (ua.indexOf("Mac") > -1) os = "macOS";
  else if (ua.indexOf("Linux") > -1) os = "Linux";
  else if (ua.indexOf("Android") > -1) os = "Android";
  else if (ua.indexOf("like Mac") > -1) os = "iOS";

  return `${browser} en ${os}`;
};

const Settings = () => {
  const { user, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "profile");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile state
  const [profile, setProfile] = useState<ProfileData>({
    id: "",
    full_name: "",
    email: "",
    phone: "",
    company: "",
    role: "lawyer",
    language: "es",
    timezone: "Europe/Madrid",
    tenant_id: null,
  });

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationPrefs>({
    email_notifications: true,
    push_notifications: true,
    case_updates: true,
    system_updates: false,
  });

  // Password change state
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [isChanging, setIsChanging] = useState(false);

  // 2FA / MFA States
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  const [qrCodeUri, setQrCodeUri] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState("");
  const [enrollingMfa, setEnrollingMfa] = useState(false);
  const [unenrollingMfa, setUnenrollingMfa] = useState(false);
  const [loadingMfa, setLoadingMfa] = useState(false);

  // Account deletion state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmationInput.trim().toUpperCase() !== "ELIMINAR MI CUENTA") {
      toast.error("Debes ingresar exactamente 'ELIMINAR MI CUENTA' para confirmar.");
      return;
    }

    setIsDeletingAccount(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/delete-account`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          confirmation: "ELIMINAR MI CUENTA"
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || "Error al eliminar la cuenta.");
      }

      toast.success("Tu cuenta y todos tus datos asociados han sido eliminados correctamente.");
      
      setDeleteDialogOpen(false);
      await signOut();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/login";
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error(error.message || "No se pudo eliminar la cuenta. Intenta nuevamente.");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Billing state
  const [subscription, setSubscription] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);

  // Team management states
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [acceptedInvites, setAcceptedInvites] = useState<any[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    full_name: "",
    role: "lawyer" as "admin" | "lawyer" | "paralegal" | "assistant" | "client",
  });

  // Tenant creation states
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState("");

  // Word templates states
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const fetchTemplates = async () => {
    if (!user) return;
    setLoadingTemplates(true);
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/templates`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error("Fallo al obtener plantillas");
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar plantillas de Word");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleUploadTemplate = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.name.endsWith(".docx")) {
      toast.error("Por favor, sube únicamente archivos de Word (.docx)");
      return;
    }
    
    setUploadingTemplate(true);
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/templates`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Fallo al subir plantilla");
      }
      
      toast.success("Plantilla subida con éxito.");
      fetchTemplates();
      window.dispatchEvent(new Event("storage-updated"));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Error al subir la plantilla.");
    } finally {
      setUploadingTemplate(false);
      e.target.value = "";
    }
  };

  const handleDeleteTemplate = async (filename: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la plantilla "${filename}"?`)) return;
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/templates/${filename}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error("Fallo al eliminar plantilla");
      toast.success("Plantilla de Word eliminada.");
      fetchTemplates();
      window.dispatchEvent(new Event("storage-updated"));
    } catch (err) {
      console.error(err);
      toast.error("Error al eliminar la plantilla.");
    }
  };

  useEffect(() => {
    if (activeTab === "templates") {
      fetchTemplates();
    }
  }, [activeTab, user]);

  const isTenantAdmin = profile?.role === "tenant_owner" || profile?.role === "admin";

  const handleCreateTenant = async () => {
    if (!newTenantName.trim()) {
      toast.error("Por favor ingresa un nombre para tu despacho.");
      return;
    }
    setCreatingTenant(true);
    try {
      const slug = newTenantName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const { data: newTenant, error: tenantErr } = await supabase
        .from("tenants")
        .insert({
          name: newTenantName.trim(),
          slug: `${slug}-${Math.floor(1000 + Math.random() * 9000)}`, // avoid collision
          user_id: user?.id
        })
        .select()
        .single();

      if (tenantErr) throw tenantErr;

      // Update profile with the new tenant_id
      const { error: profileErr } = await supabase
        .from("profiles")
        .update({ 
          tenant_id: newTenant.id,
          company: newTenantName.trim(),
          role: "tenant_owner"
        })
        .eq("id", user?.id);

      if (profileErr) throw profileErr;

      toast.success("Despacho creado y configurado exitosamente.");
      setProfile(prev => ({ 
        ...prev, 
        tenant_id: newTenant.id,
        company: newTenantName.trim(),
        role: "tenant_owner"
      }));
      await fetchTeamData(newTenant.id);
    } catch (err: any) {
      console.error("Error creating tenant:", err);
      toast.error(err.message || "Error al crear el despacho.");
    } finally {
      setCreatingTenant(false);
    }
  };

  const fetchTeamData = async (tenantId: string) => {
    if (!tenantId) return;
    setLoadingTeam(true);
    try {
      const { data: members, error: membersErr } = await supabase
        .from("profiles")
        .select("*")
        .eq("tenant_id", tenantId);

      if (membersErr) throw membersErr;
      setTeamMembers(members || []);

      const { data: invites, error: invitesErr } = await supabase
        .from("tenant_invites")
        .select("*")
        .eq("tenant_id", tenantId);

      if (invitesErr) {
        console.warn("Error fetching invites:", invitesErr);
        setPendingInvites([]);
        setAcceptedInvites([]);
      } else {
        setPendingInvites(invites?.filter(i => i.status === "pending") || []);
        setAcceptedInvites(invites?.filter(i => i.status === "accepted") || []);
      }
    } catch (err) {
      console.error("Error fetching team data:", err);
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleSendInvite = async () => {
    if (!profile.tenant_id) {
      toast.error("No tienes un despacho/tenant configurado.");
      return;
    }

    const totalMembers = teamMembers.length + pendingInvites.length;
    const limit = subscription?.max_team_members || 1;
    if (totalMembers >= limit) {
      toast.error(`Límite alcanzado. Tu plan actual permite hasta ${limit} miembros.`);
      return;
    }

    if (!inviteForm.email || !inviteForm.full_name) {
      toast.error("Por favor completa todos los campos.");
      return;
    }

    setSendingInvite(true);
    try {
      const { error } = await supabase
        .from("tenant_invites")
        .insert({
          tenant_id: profile.tenant_id,
          email: inviteForm.email.trim(),
          full_name: inviteForm.full_name.trim(),
          role: inviteForm.role,
          invited_by: user?.id,
          status: "pending"
        });

      if (error) throw error;

      // Call primary backend service to send the invitation email via OCI SMTP
      try {
        const token = await getAccessToken();
        const emailResponse = await fetch(`${API_BASE_URL}/api/tenant-invite/send`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            email: inviteForm.email.trim(),
            full_name: inviteForm.full_name.trim(),
            role: inviteForm.role,
            tenant_id: profile.tenant_id,
            tenant_name: profile.company || "Mi Despacho",
            inviter_name: profile.full_name || "Un miembro de tu equipo",
          }),
        });

        if (!emailResponse.ok) {
          const errData = await emailResponse.json().catch(() => ({}));
          console.warn("Failed to send invite email via OCI SMTP:", errData);
          toast.warning("Invitación guardada, pero falló el envío del correo de notificación.");
        } else {
          toast.success("Correo de invitación enviado.");
        }
      } catch (emailErr) {
        console.error("Error calling primary backend email service:", emailErr);
        toast.warning("Invitación guardada, pero no se pudo contactar al servicio de correo.");
      }

      toast.success("Invitación registrada exitosamente.");
      setInviteForm({ email: "", full_name: "", role: "lawyer" });
      await fetchTeamData(profile.tenant_id);
    } catch (err: any) {
      console.error("Error sending invite:", err);
      toast.error(err.message || "Error al enviar la invitación.");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      const { error } = await supabase
        .from("tenant_invites")
        .delete()
        .eq("id", inviteId);

      if (error) throw error;
      toast.success("Invitación cancelada.");
      if (profile.tenant_id) await fetchTeamData(profile.tenant_id);
    } catch (err) {
      console.error("Error cancelling invite:", err);
      toast.error("Error al cancelar la invitación.");
    }
  };

  const handleDeleteInviteHistory = async (inviteId: string) => {
    try {
      const { error } = await supabase
        .from("tenant_invites")
        .delete()
        .eq("id", inviteId);

      if (error) throw error;
      toast.success("Registro de invitación eliminado del historial.");
      if (profile.tenant_id) await fetchTeamData(profile.tenant_id);
    } catch (err) {
      console.error("Error deleting invite history:", err);
      toast.error("Error al eliminar el historial de la invitación.");
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase.rpc("remove_tenant_member", {
        p_member_id: memberId
      });

      if (error) throw error;
      toast.success("Miembro removido exitosamente.");
      if (profile.tenant_id) await fetchTeamData(profile.tenant_id);
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "Error al remover miembro.");
    }
  };

  useEffect(() => {
    if (user) {
      fetchSettingsData();
    }
  }, [user]);

  const fetchSettingsData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Fetch Profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      let tenantId: string | null = null;

      if (profileData) {
        tenantId = profileData.tenant_id;
        
        // If profile doesn't have tenant_id, check if a tenant owned by the user exists
        if (!tenantId && user) {
          try {
            const { data: tenantData } = await supabase
              .from("tenants")
              .select("id")
              .eq("user_id", user.id)
              .maybeSingle();

            if (tenantData) {
              tenantId = tenantData.id;
              // Auto-update profile
              await supabase
                .from("profiles")
                .update({ tenant_id: tenantId })
                .eq("id", user.id);
            }
          } catch (tenantQueryErr) {
            console.warn("Could not auto-link tenant:", tenantQueryErr);
          }
        }

        setProfile({
          id: profileData.id,
          full_name: profileData.full_name || "",
          email: profileData.email || user.email || "",
          phone: profileData.phone || "",
          company: profileData.company || "",
          role: profileData.role || "lawyer",
          language: profileData.language || "es",
          timezone: profileData.timezone || "Europe/Madrid",
          tenant_id: tenantId,
        });
      } else if (profileError && profileError.code === "PGRST116") {
        // Create profile if doesn't exist
        const newProfile = {
          id: user.id,
          full_name: user.user_metadata?.full_name || "",
          email: user.email,
          role: "lawyer" as const,
          language: "es",
          timezone: "Europe/Madrid",
        };
        await supabase.from("profiles").insert([newProfile]);
        setProfile({ ...newProfile, phone: "", company: "", tenant_id: null });
      }

      // Fetch Notifications
      const { data: notifData } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (notifData) {
        setNotifications({
          email_notifications: notifData.email_notifications,
          push_notifications: notifData.push_notifications,
          case_updates: notifData.case_updates,
          system_updates: notifData.system_updates,
        });
      }

      // Fetch MFA / 2FA status
      try {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const verifiedFactor = factors?.totp?.find(f => f.status === "verified");
        if (verifiedFactor) {
          setTwoFactorEnabled(true);
          setFactorId(verifiedFactor.id);
        } else {
          setTwoFactorEnabled(false);
          setFactorId(null);
        }
      } catch (mfaErr) {
        console.warn("Error checking MFA factors:", mfaErr);
      }

      // Fetch Subscription with Plan details
      const { data: subData } = await supabase
        .from("subscriptions")
        .select(`
          *,
          plans:plan_id (
            name,
            price_per_month,
            currency,
            max_team_members
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();
      
      if (subData) {
        // Map joined data to the subscription object for easier access
        const mappedSub = {
          ...subData,
          plan_name: subData.plans?.name || subData.plan_name,
          price_per_month: subData.plans?.price_per_month || subData.price_per_month,
          currency: subData.plans?.currency || "COP $",
          renewal_date: subData.ends_at || subData.trial_ends_at,
          max_team_members: subData.plans?.max_team_members || 1
        };
        setSubscription(mappedSub);
      }

      // Fetch Invoices
      const { data: invoiceData } = await supabase
        .from("invoices")
        .select("*")
        .eq("user_id", user.id)
        .order("invoice_date", { ascending: false });
      
      if (invoiceData) setInvoices(invoiceData);

      // Fetch team data if tenantId is present
      if (tenantId) {
        await fetchTeamData(tenantId);
      }

    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name,
          phone: profile.phone,
          company: profile.company,
          role: profile.role as any,
          language: profile.language,
          timezone: profile.timezone,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;
      toast.success("Perfil actualizado correctamente");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = async (key: keyof NotificationPrefs) => {
    if (!user) return;
    const newValue = !notifications[key];

    // If turning on push notifications, verify and request browser permission
    if (key === "push_notifications" && newValue) {
      if (!("Notification" in window)) {
        toast.error("Tu navegador no soporta notificaciones de escritorio/push.");
        return;
      }
      if (Notification.permission === "denied") {
        toast.error("Las notificaciones están bloqueadas en tu navegador. Habilítalas en los permisos del sitio.");
        return;
      }
      if (Notification.permission !== "granted") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          toast.warning("Debes permitir las notificaciones en el navegador para activar esta opción.");
          return;
        }
      }
    }

    setNotifications(prev => ({ ...prev, [key]: newValue }));

    try {
      const { error: sbError } = await supabase
        .from("notification_preferences")
        .upsert({
          user_id: user.id,
          [key]: newValue,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (sbError) {
        // Fallback resiliente al endpoint administrativo de backend
        const token = await getAccessToken();
        const res = await fetch(`${API_BASE_URL}/api/v1/auth/notification-preferences`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ [key]: newValue })
        });
        if (!res.ok) throw sbError;
      }

      toast.success("Preferencia actualizada correctamente.");
    } catch (error) {
      console.error("Error updating notification preference:", error);
      toast.error("Error al actualizar preferencias");
      // Revert state on error
      setNotifications(prev => ({ ...prev, [key]: !newValue }));
    }
  };

  const handleStartEnrollMfa = async () => {
    setLoadingMfa(true);
    try {
      // Limpiar factores previos no verificados
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors?.all) {
        for (const f of factors.all) {
          if (f.status === "unverified") {
            await supabase.auth.mfa.unenroll({ factorId: f.id });
          }
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        issuer: "SARAI Legal",
      });
      if (error) throw error;

      setFactorId(data.id);
      setQrCodeUri(data.totp.qr_code);
      setSecretKey(data.totp.secret);
      setMfaVerifyCode("");
      setEnrollDialogOpen(true);
    } catch (err: any) {
      console.error("Error al iniciar enrolamiento 2FA:", err);
      toast.error(err.message || "No se pudo iniciar la configuración de 2FA.");
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleConfirmEnrollMfa = async () => {
    if (!factorId || mfaVerifyCode.trim().length < 6) {
      toast.error("Por favor ingresa el código de 6 dígitos.");
      return;
    }
    setEnrollingMfa(true);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: mfaVerifyCode.trim(),
      });
      if (error) throw error;

      setTwoFactorEnabled(true);
      setEnrollDialogOpen(false);
      toast.success("¡Autenticación de dos factores (2FA) activada correctamente!");
    } catch (err: any) {
      toast.error("Código incorrecto o expirado. Inténtalo de nuevo.");
    } finally {
      setEnrollingMfa(false);
    }
  };

  const handleUnenrollMfa = async () => {
    if (!factorId) return;
    setUnenrollingMfa(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      setTwoFactorEnabled(false);
      setFactorId(null);
      setUnenrollDialogOpen(false);
      toast.success("Autenticación 2FA desactivada.");
    } catch (err: any) {
      toast.error(err.message || "Error al desactivar 2FA.");
    } finally {
      setUnenrollingMfa(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      toast.error("Por favor, completa todos los campos");
      return;
    }

    if (passwords.new !== passwords.confirm) {
      toast.error("Las nuevas contraseñas no coinciden");
      return;
    }

    if (passwords.new.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setIsChanging(true);
    try {
      // Re-autenticar con la contraseña actual para verificar identidad
      const { error: reauthErr } = await supabase.auth.signInWithPassword({
        email: user?.email || profile.email,
        password: passwords.current,
      });

      if (reauthErr) {
        toast.error("La contraseña actual es incorrecta.");
        setIsChanging(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: passwords.new,
      });

      if (error) throw error;
      
      toast.success("Contraseña actualizada correctamente");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (error: any) {
      toast.error(error.message || "Error al cambiar la contraseña");
    } finally {
      setIsChanging(false);
    }
  };

  const handleSignOutAll = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      toast.success("Has cerrado sesión en todos tus dispositivos");
      // Redirect will happen automatically if handled by AuthContext
    } catch (error) {
      console.error("Error signing out all sessions:", error);
      toast.error("Error al cerrar todas las sesiones");
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-legal-blue" />
        <p className="text-gray-500 font-medium">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-legal-blue to-legal-navy flex items-center justify-center shadow-lg shadow-legal-blue/20">
            <SettingsIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-legal-navy">Configuración</h2>
            <p className="text-sm text-gray-500">Gestiona tu cuenta y preferencias personales</p>
          </div>
        </div>
      </div>

      <Tabs
        defaultValue="profile"
        className="w-full"
        onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-[900px] bg-gray-100/50 p-1 rounded-xl">
          <TabsTrigger value="profile" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Perfil</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Lock className="h-4 w-4" />
            <span className="hidden sm:inline">Seguridad</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Notificaciones</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Facturación</span>
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Equipo</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center space-x-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Plantillas de Word</span>
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="profile" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Card className="border-gray-100 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                <CardTitle className="text-lg">Información Personal</CardTitle>
                <CardDescription>
                  Actualiza tu información personal y cómo te verán otros usuarios
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-sm font-semibold text-gray-700">Nombre Completo</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        id="full_name" 
                        name="full_name"
                        value={profile.full_name}
                        onChange={handleProfileChange}
                        placeholder="Tu nombre completo" 
                        className="pl-10 rounded-xl border-gray-200 focus:ring-legal-blue"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        id="email" 
                        type="email" 
                        value={profile.email}
                        disabled
                        className="pl-10 rounded-xl bg-gray-50 border-gray-100 text-gray-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        id="phone" 
                        name="phone"
                        type="tel" 
                        value={profile.phone}
                        onChange={handleProfileChange}
                        placeholder="+34 XXX XXX XXX" 
                        className="pl-10 rounded-xl border-gray-200 focus:ring-legal-blue"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-sm font-semibold text-gray-700">Empresa / Despacho</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        id="company" 
                        name="company"
                        value={profile.company}
                        onChange={handleProfileChange}
                        placeholder="Nombre de tu empresa" 
                        className="pl-10 rounded-xl border-gray-200 focus:ring-legal-blue"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-sm font-semibold text-gray-700">Cargo</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        id="role" 
                        name="role"
                        value={profile.role}
                        onChange={handleProfileChange}
                        placeholder="Abogado, Socio, etc." 
                        className="pl-10 rounded-xl border-gray-200 focus:ring-legal-blue"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language" className="text-sm font-semibold text-gray-700">Idioma</Label>
                    <Select value={profile.language} onValueChange={(val) => setProfile(prev => ({ ...prev, language: val }))}>
                      <SelectTrigger className="rounded-xl border-gray-200">
                        <SelectValue placeholder="Selecciona un idioma" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    className="rounded-xl px-8 bg-legal-blue hover:bg-legal-navy shadow-md shadow-legal-blue/20"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      "Guardar Cambios"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Card className="border-gray-100 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                <CardTitle className="text-lg">Seguridad de la Cuenta</CardTitle>
                <CardDescription>
                  Gestiona tu contraseña y la seguridad de acceso
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="current" className="text-sm font-semibold text-gray-700">Contraseña Actual</Label>
                    <Input 
                      id="current" 
                      type="password" 
                      value={passwords.current}
                      onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                      placeholder="••••••••"
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new" className="text-sm font-semibold text-gray-700">Nueva Contraseña</Label>
                    <Input 
                      id="new" 
                      type="password" 
                      value={passwords.new}
                      onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                      placeholder="••••••••"
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm" className="text-sm font-semibold text-gray-700">Confirmar Nueva</Label>
                    <Input 
                      id="confirm" 
                      type="password" 
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                      placeholder="••••••••"
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    variant="outline"
                    className="rounded-xl px-8 border-legal-blue text-legal-blue hover:bg-legal-blue/5"
                    onClick={handlePasswordChange}
                    disabled={isChanging}
                  >
                    {isChanging ? "Actualizando..." : "Actualizar Contraseña"}
                  </Button>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold text-gray-900">Autenticación de Dos Factores (2FA)</p>
                        {twoFactorEnabled && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs font-semibold">
                            Activo
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {twoFactorEnabled 
                          ? "Tu cuenta está protegida con código TOTP (Google Authenticator / Authy)" 
                          : "Añade una capa extra de seguridad a tu cuenta mediante una aplicación de autenticación"}
                      </p>
                    </div>
                    <Switch 
                      checked={twoFactorEnabled} 
                      onCheckedChange={() => {
                        if (twoFactorEnabled) setUnenrollDialogOpen(true);
                        else handleStartEnrollMfa();
                      }}
                      disabled={loadingMfa || enrollingMfa || unenrollingMfa}
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" /> Sesiones Activas
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:bg-red-50 hover:text-red-600 h-8 rounded-lg text-xs font-bold"
                      onClick={handleSignOutAll}
                    >
                      Cerrar todas las sesiones
                    </Button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50/50">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Esta sesión ({getBrowserInfo()})</p>
                          <p className="text-xs text-gray-500">Última actividad: Ahora mismo</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-red-100">
                  <div className="p-6 rounded-2xl bg-red-50/50 border border-red-200/80 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <h3 className="font-bold text-red-900 flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                          Zona de Peligro: Eliminar Cuenta
                        </h3>
                        <p className="text-sm text-red-700 max-w-xl">
                          Elimina permanentemente tu cuenta y todos los datos asociados: consultas, documentos, procesos judiciales vigilados, suscripciones, historial y notificaciones. <strong>Esta acción no se puede deshacer.</strong>
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shrink-0 shadow-md shadow-red-600/20"
                        onClick={() => {
                          setDeleteConfirmationInput("");
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar Cuenta
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent className="max-w-md rounded-2xl p-6">
                <DialogHeader className="space-y-2">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mx-auto sm:mx-0">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">
                    ¿Eliminar tu cuenta definitivamente?
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-600 leading-relaxed">
                    Esta acción es <strong className="text-red-600">irreversible</strong>. Se eliminarán permanentemente de la base de datos todos los registros asociados a tu usuario:
                  </DialogDescription>
                </DialogHeader>

                <div className="bg-red-50/70 p-3 rounded-xl border border-red-100 text-xs text-red-800 space-y-1">
                  <p className="font-bold">Datos que serán eliminados permanentemente:</p>
                  <ul className="list-disc pl-4 space-y-0.5 text-red-700">
                    <li>Consultas de IA e historial de chats legales</li>
                    <li>Documentos guardados y plantillas membretadas</li>
                    <li>Procesos judiciales y vigilancia de expedientes</li>
                    <li>Suscripciones, saldo de créditos y facturación</li>
                    <li>Perfil de usuario y preferencias</li>
                  </ul>
                </div>

                <div className="space-y-3 py-2">
                  <Label htmlFor="delete-confirm" className="text-xs font-bold text-gray-700">
                    Para confirmar la eliminación, escribe <span className="text-red-600 font-mono font-bold">ELIMINAR MI CUENTA</span> a continuación:
                  </Label>
                  <Input
                    id="delete-confirm"
                    value={deleteConfirmationInput}
                    onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                    placeholder="ELIMINAR MI CUENTA"
                    className="rounded-xl border-red-200 focus:border-red-500 focus:ring-red-500 font-medium"
                  />
                </div>

                <DialogFooter className="gap-2 sm:gap-0 mt-2">
                  <Button
                    variant="outline"
                    className="rounded-xl border-gray-200"
                    onClick={() => setDeleteDialogOpen(false)}
                    disabled={isDeletingAccount}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                    onClick={handleDeleteAccount}
                    disabled={
                      deleteConfirmationInput.trim().toUpperCase() !== "ELIMINAR MI CUENTA" ||
                      isDeletingAccount
                    }
                  >
                    {isDeletingAccount ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      "Confirmar Eliminación"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Dialog Enrolar 2FA */}
            <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
              <DialogContent className="max-w-md rounded-2xl p-6">
                <DialogHeader className="space-y-2">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-legal-blue mx-auto sm:mx-0">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">
                    Configurar Autenticación 2FA
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-600 leading-relaxed">
                    Escanea el código QR desde tu app de autenticación (Google Authenticator, Authy o Apple Passwords):
                  </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3">
                  {qrCodeUri && (
                    <img src={qrCodeUri} alt="Código QR 2FA" className="w-48 h-48 rounded-xl shadow-sm border border-gray-200" />
                  )}
                  {secretKey && (
                    <div className="text-center w-full">
                      <p className="text-xs text-gray-400 font-medium mb-1">¿No puedes escanear el código? Usa esta clave:</p>
                      <code className="text-xs font-mono bg-white px-2.5 py-1 rounded-lg border border-gray-200 select-all text-gray-800 break-all inline-block max-w-full">
                        {secretKey}
                      </code>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mfa-code" className="text-xs font-bold text-gray-700">
                    Ingresa el código de 6 dígitos generado por tu app:
                  </Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    maxLength={6}
                    value={mfaVerifyCode}
                    onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="text-center text-xl tracking-widest font-mono rounded-xl border-gray-200"
                  />
                </div>

                <DialogFooter className="gap-2 sm:gap-0 mt-2">
                  <Button variant="outline" className="rounded-xl border-gray-200" onClick={() => setEnrollDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button className="rounded-xl bg-legal-blue hover:bg-legal-navy text-white font-bold" onClick={handleConfirmEnrollMfa} disabled={enrollingMfa || mfaVerifyCode.trim().length < 6}>
                    {enrollingMfa ? "Verificando..." : "Verificar y Activar"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Dialog Desactivar 2FA */}
            <Dialog open={unenrollDialogOpen} onOpenChange={setUnenrollDialogOpen}>
              <DialogContent className="max-w-md rounded-2xl p-6">
                <DialogHeader className="space-y-2">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mx-auto sm:mx-0">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <DialogTitle className="text-xl font-bold text-gray-900">
                    ¿Desactivar la Autenticación 2FA?
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-600 leading-relaxed">
                    Tu cuenta perderá la capa de seguridad adicional y solo requerirá contraseña para ingresar.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0 mt-2">
                  <Button variant="outline" className="rounded-xl border-gray-200" onClick={() => setUnenrollDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button variant="destructive" className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleUnenrollMfa} disabled={unenrollingMfa}>
                    {unenrollingMfa ? "Desactivando..." : "Sí, desactivar 2FA"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Card className="border-gray-100 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                <CardTitle className="text-lg">Preferencias de Notificaciones</CardTitle>
                <CardDescription>
                  Elige qué notificaciones quieres recibir y por qué medio
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl border border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-900">Notificaciones por Email</p>
                        <p className="text-sm text-gray-500 text-balance max-w-md">Recibe resúmenes diarios y alertas críticas en tu bandeja de entrada</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifications.email_notifications}
                      onCheckedChange={() => handleNotificationToggle("email_notifications")}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl border border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                        <Bell className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-900">Notificaciones Push</p>
                        <p className="text-sm text-gray-500 text-balance max-w-md">Recibe alertas en tiempo real en tu navegador o dispositivo móvil</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifications.push_notifications}
                      onCheckedChange={() => handleNotificationToggle("push_notifications")}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl border border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-900">Actualizaciones de Casos</p>
                        <p className="text-sm text-gray-500 text-balance max-w-md">Avisar cuando haya cambios significativos en tus expedientes vigilados</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifications.case_updates}
                      onCheckedChange={() => handleNotificationToggle("case_updates")}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl border border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-amber-600" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-900">Actualizaciones del Sistema</p>
                        <p className="text-sm text-gray-500 text-balance max-w-md">Nuevas funciones, mejoras y avisos de mantenimiento</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifications.system_updates}
                      onCheckedChange={() => handleNotificationToggle("system_updates")}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 border-gray-100 shadow-sm overflow-hidden">
                <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                  <CardTitle className="text-lg">Plan y Suscripción</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-legal-blue to-legal-navy text-white shadow-xl shadow-legal-blue/20">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <p className="text-blue-100 text-sm font-medium uppercase tracking-wider">Plan Actual</p>
                        <h3 className="text-3xl font-bold mt-1">
                          {subscription?.plan_name || "Plan Gratuito / Trial"}
                        </h3>
                      </div>
                      <Badge className="bg-white/20 text-white border-white/20 px-3 py-1">
                        {subscription?.status || "Activo"}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                      <div>
                        <p className="text-blue-100 text-xs">Monto por mes</p>
                        <p className="text-lg font-bold">
                          {subscription?.currency || "$"}{subscription?.price_per_month?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-100 text-xs">Próxima renovación</p>
                        <p className="text-lg font-bold">
                          {subscription?.renewal_date ? new Date(subscription.renewal_date).toLocaleDateString() : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Métodos de Pago</h3>
                    <div className="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50/30">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-8 bg-white border border-gray-100 rounded-md flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">•••• •••• •••• 4242</p>
                          <p className="text-xs text-gray-500">Expira: 12/25</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-500">Eliminar</Button>
                    </div>
                    <Button variant="outline" className="w-full rounded-xl border-dashed border-2 hover:bg-gray-50">
                      Añadir Método de Pago
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-100 shadow-sm overflow-hidden">
                <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                  <CardTitle className="text-lg">Historial</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {invoices.length > 0 ? invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                        <div>
                          <p className="text-sm font-bold text-gray-900">Factura #{inv.invoice_number}</p>
                          <p className="text-xs text-gray-500">{new Date(inv.invoice_date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-legal-navy">€{inv.amount}</p>
                          <Button variant="link" className="h-auto p-0 text-[10px] text-legal-blue">Descargar</Button>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-400">No hay facturas disponibles</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="team" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {!profile.tenant_id ? (
              <div className="max-w-md mx-auto py-12">
                <Card className="border-gray-100 shadow-sm overflow-hidden">
                  <CardHeader className="text-center pb-2 bg-gray-50/30 border-b border-gray-50">
                    <Building className="h-10 w-10 text-legal-blue mx-auto mb-2" />
                    <CardTitle className="text-xl">Configurar Despacho</CardTitle>
                    <CardDescription>
                      Para comenzar a gestionar tu equipo, primero debes configurar el nombre de tu despacho o empresa.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="tenant-name" className="text-sm font-semibold text-gray-700">Nombre de tu Despacho / Firma</Label>
                      <Input
                        id="tenant-name"
                        value={newTenantName}
                        onChange={(e) => setNewTenantName(e.target.value)}
                        placeholder="Ej. Consultores Jurídicos Asociados"
                        className="rounded-xl border-gray-200"
                      />
                    </div>
                    <Button
                      className="w-full rounded-xl bg-legal-blue hover:bg-legal-navy text-white font-bold"
                      onClick={handleCreateTenant}
                      disabled={creatingTenant}
                    >
                      {creatingTenant ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creando despacho...
                        </>
                      ) : (
                        "Crear Despacho"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : !isTenantAdmin ? (
              <div className="max-w-2xl mx-auto">
                <Card className="border-gray-100 shadow-sm overflow-hidden">
                  <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-legal-blue" />
                      <CardTitle className="text-lg">Mi Despacho</CardTitle>
                    </div>
                    <CardDescription>Información sobre tu afiliación actual</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="p-6 rounded-xl bg-slate-50 border border-slate-100 flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 rounded-full bg-legal-blue/10 flex items-center justify-center text-legal-blue">
                        <Building className="h-8 w-8" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Despacho / Empresa</p>
                        <p className="text-xl font-bold text-slate-800">{profile.company || "Mi Despacho"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tu Rol</p>
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs font-bold uppercase py-1 px-3">
                          {profile.role === "tenant_owner" ? "Propietario" :
                           profile.role === "admin" ? "Administrador" :
                           profile.role === "lawyer" ? "Abogado" :
                           profile.role === "paralegal" ? "Paralegal" :
                           profile.role === "assistant" ? "Asistente" :
                           profile.role === "client" ? "Cliente" : profile.role}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 max-w-md pt-2 border-t border-slate-200/60 mt-2">
                        Perteneces a este despacho como miembro del equipo. Solo el administrador del despacho puede invitar a nuevos miembros o cambiar la configuración de la suscripción.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form to Invite Member / Subscription Info */}
                <div className="space-y-6 lg:col-span-1">
                  <Card className="border-gray-100 shadow-sm overflow-hidden">
                    <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                      <CardTitle className="text-lg">Información del Plan</CardTitle>
                      <CardDescription>Capacidad del equipo y límites</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-4">
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Capacidad del Equipo</p>
                          <p className="text-2xl font-black text-slate-900 mt-1">
                            {teamMembers.length + pendingInvites.length}
                            <span className="text-sm text-slate-400 font-medium ml-1">
                              / {subscription?.max_team_members || 1} miembros
                            </span>
                          </p>
                          <div className="h-2 w-full bg-slate-200 rounded-full mt-3 overflow-hidden">
                            <div
                              className="h-full bg-legal-blue rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(
                                  (((teamMembers.length + pendingInvites.length) / (subscription?.max_team_members || 1)) * 100),
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>

                        <div className="pt-3 border-t border-slate-200/60 space-y-2 text-xs text-slate-600">
                          <div className="flex justify-between">
                            <span>Administrador / Propietario:</span>
                            <span className="font-bold text-slate-900">1</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Miembros Activos:</span>
                            <span className="font-bold text-slate-900">{Math.max(0, teamMembers.length - 1)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Invitaciones Pendientes:</span>
                            <span className="font-bold text-slate-900">{pendingInvites.length}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Invitaciones Aceptadas:</span>
                            <span className="font-bold text-slate-900">{acceptedInvites.length}</span>
                          </div>
                        </div>
                      </div>

                      {(subscription?.max_team_members || 1) <= 1 && (
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs leading-relaxed space-y-3">
                          <p className="font-bold flex items-center gap-1.5">
                            <Shield className="h-4 w-4 shrink-0 text-amber-600" />
                            Plan Limitado
                          </p>
                          <p>
                            Tu plan actual permite solo 1 usuario. Para agregar paralegales, asistentes u otros abogados, por favor actualiza tu suscripción.
                          </p>
                          <Button
                            size="sm"
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider"
                            onClick={() => {
                              setActiveTab("billing");
                            }}
                          >
                            Ver Planes Disponibles
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Invite Form */}
                  {isTenantAdmin && (subscription?.max_team_members || 1) > 1 && (
                    <Card className="border-gray-100 shadow-sm overflow-hidden">
                      <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <UserPlus className="h-4 w-4 text-legal-blue" />
                          Invitar Miembro
                        </CardTitle>
                        <CardDescription>Envía una invitación a tu equipo</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="invite-name" className="text-sm font-semibold text-gray-700">Nombre Completo</Label>
                          <Input
                            id="invite-name"
                            value={inviteForm.full_name}
                            onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                            placeholder="Ej. Carlos Mendoza"
                            className="rounded-xl border-gray-200"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="invite-email" className="text-sm font-semibold text-gray-700">Correo Electrónico</Label>
                          <Input
                            id="invite-email"
                            type="email"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="carlos@despacho.com"
                            className="rounded-xl border-gray-200"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="invite-role" className="text-sm font-semibold text-gray-700">Rol</Label>
                          <Select
                            value={inviteForm.role}
                            onValueChange={(val: any) => setInviteForm({ ...inviteForm, role: val })}
                          >
                            <SelectTrigger className="rounded-xl border-gray-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lawyer">Abogado</SelectItem>
                              <SelectItem value="paralegal">Paralegal</SelectItem>
                              <SelectItem value="assistant">Asistente</SelectItem>
                              <SelectItem value="client">Cliente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <Button
                          className="w-full rounded-xl bg-legal-blue hover:bg-legal-navy shadow-md shadow-legal-blue/10 text-white mt-2 font-bold"
                          onClick={handleSendInvite}
                          disabled={sendingInvite || (teamMembers.length + pendingInvites.length) >= (subscription?.max_team_members || 1)}
                        >
                          {sendingInvite ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Invitando...
                            </>
                          ) : (
                            "Enviar Invitación"
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Members List & Invites List */}
                <div className="space-y-6 lg:col-span-2">
                  <Card className="border-gray-100 shadow-sm overflow-hidden">
                    <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-4 w-4 text-legal-blue" />
                        Miembros Activos
                      </CardTitle>
                      <CardDescription>Usuarios con acceso actual al despacho</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {loadingTeam ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-legal-blue" />
                        </div>
                      ) : teamMembers.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">No hay miembros registrados.</p>
                      ) : (
                        <div className="space-y-3">
                          {teamMembers.map((member) => (
                            <div key={member.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50/30 hover:bg-gray-50/60 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-700">
                                  {member.full_name ? member.full_name.charAt(0).toUpperCase() : "?"}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{member.full_name || "Sin nombre"}</p>
                                  <p className="text-xs text-gray-500">{member.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={cn("text-[10px] font-bold uppercase", 
                                  member.role === "tenant_owner" ? "bg-amber-100 text-amber-800 border-amber-300" :
                                  member.role === "admin" ? "bg-red-50 text-red-700 border-red-200" :
                                  member.role === "lawyer" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                  member.role === "paralegal" ? "bg-purple-50 text-purple-700 border-purple-200" :
                                  member.role === "assistant" ? "bg-slate-50 text-slate-700 border-slate-200" :
                                  "bg-green-50 text-green-700 border-green-200"
                                )}>
                                  {member.role === "tenant_owner" ? "Propietario" :
                                   member.role === "admin" ? "Administrador" :
                                   member.role === "lawyer" ? "Abogado" :
                                   member.role === "paralegal" ? "Paralegal" :
                                   member.role === "assistant" ? "Asistente" : "Cliente"}
                                </Badge>

                                {isTenantAdmin && member.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:bg-red-50 rounded-lg"
                                    onClick={() => handleRemoveMember(member.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Pending Invites List */}
                  {pendingInvites.length > 0 && (
                    <Card className="border-gray-100 shadow-sm overflow-hidden">
                      <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                        <CardTitle className="text-lg">Invitaciones Pendientes</CardTitle>
                        <CardDescription>Usuarios invitados que aún no se han registrado</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          {pendingInvites.map((invite) => (
                            <div key={invite.id} className="flex items-center justify-between p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/20">
                              <div>
                                <p className="text-sm font-bold text-gray-800">{invite.full_name}</p>
                                <p className="text-xs text-gray-500">{invite.email}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="text-[10px] uppercase font-bold text-amber-600 bg-amber-50 border-amber-200">
                                  Pendiente
                                </Badge>
                                {isTenantAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 h-8 rounded-lg font-bold text-xs"
                                    onClick={() => handleCancelInvite(invite.id)}
                                  >
                                    Cancelar
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Accepted Invites List */}
                  {acceptedInvites.length > 0 && (
                    <Card className="border-gray-100 shadow-sm overflow-hidden">
                      <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                        <CardTitle className="text-lg">Invitaciones Aceptadas</CardTitle>
                        <CardDescription>Historial de usuarios que han aceptado tu invitación</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          {acceptedInvites.map((invite) => (
                            <div key={invite.id} className="flex items-center justify-between p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/20">
                              <div>
                                <p className="text-sm font-bold text-gray-800">{invite.full_name}</p>
                                <p className="text-xs text-gray-500">{invite.email}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="text-[10px] uppercase font-bold text-green-600 bg-green-50 border-green-200">
                                  Aceptada
                                </Badge>
                                <span className="text-xs text-gray-400">
                                  {invite.accepted_at ? new Date(invite.accepted_at).toLocaleDateString() : ""}
                                </span>
                                {isTenantAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:bg-red-50 rounded-lg"
                                    onClick={() => handleDeleteInviteHistory(invite.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Card className="border-gray-100 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-gray-50 bg-gray-50/30">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <span>Plantillas de Hoja Membretada (.docx)</span>
                </CardTitle>
                <CardDescription>
                  Carga el diseño membretado de tu firma u organización para formatear de manera corporativa tus documentos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                
                {/* Instructions section */}
                <div className="bg-gradient-to-br from-blue-50/60 to-indigo-50/40 p-5 rounded-2xl border border-blue-100/50 space-y-3">
                  <h4 className="text-sm font-bold text-blue-800 flex items-center gap-1.5">
                    <FileCheck className="h-4 w-4" />
                    <span>¿Cómo preparar tu plantilla?</span>
                  </h4>
                  <ul className="text-xs text-blue-700/80 space-y-2 list-decimal pl-4 leading-relaxed font-medium">
                    <li>Prepara tu documento de Word (.docx) con el diseño de tu cabecera, pie de página y logo corporativo.</li>
                    <li>Escribe el marcador <code className="bg-white/80 px-1.5 py-0.5 rounded font-mono border border-blue-200/50 text-blue-900 font-bold">{"{{contenido}}"}</code> en el lugar exacto del documento donde deba ir la redacción legal.</li>
                    <li>Sube el archivo aquí. Al exportar en el editor, selecciona <strong>Word con membrete</strong> y descarga tu archivo listo.</li>
                  </ul>
                </div>

                {/* Upload Action */}
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-blue-400/80 transition-all rounded-2xl p-8 bg-gray-50/30">
                  <input
                    type="file"
                    id="template-upload"
                    className="hidden"
                    accept=".docx"
                    onChange={handleUploadTemplate}
                    disabled={uploadingTemplate}
                  />
                  <label
                    htmlFor="template-upload"
                    className="flex flex-col items-center cursor-pointer space-y-2"
                  >
                    <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                      {uploadingTemplate ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Plus className="h-5 w-5" />
                      )}
                    </div>
                    <span className="text-xs font-bold text-gray-700">
                      {uploadingTemplate ? "Subiendo archivo..." : "Cargar plantilla membretada (.docx)"}
                    </span>
                    <span className="text-[10px] text-gray-400">Máximo 10MB • Solo formato Word (.docx)</span>
                  </label>
                </div>

                {/* Templates List */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Mis Plantillas Membretadas</h4>
                  
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center py-10 border border-gray-100 rounded-2xl bg-gray-50/10">
                      <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs font-medium text-gray-500">No tienes plantillas subidas aún.</p>
                      <p className="text-[10px] text-gray-400 mt-1">Carga tu primera plantilla para comenzar.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                      {templates.map((template, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-white hover:bg-gray-50/50 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                              <FileText className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-800">{template.name}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                Subido el: {new Date(template.created_at).toLocaleDateString("es-CO", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric"
                                })}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:bg-red-50 rounded-lg"
                            onClick={() => handleDeleteTemplate(template.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      <div className="flex justify-end pt-6">
        <Button 
          variant="ghost" 
          className="text-red-500 hover:bg-red-50 rounded-xl"
          onClick={signOut}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Cerrar Sesión
        </Button>
      </div>
    </div>
  );
};

export default Settings;
